import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Grid, Typography,
} from '@mui/material';
// eslint-disable-next-line import/no-cycle
import EditorControls from '../editorControls';
// eslint-disable-next-line import/no-cycle
import WidthInput from './width_input';

function FormatEditor({
  value, onChange, allowedFonts,
}) {
  const dFonts = useMemo(
    () => allowedFonts.map((v) => ({
      value: v,
      display_name: v,
    })),
    [allowedFonts],
  );
  const hAligns = useMemo(
    () => [
      {
        value: 'left',
        display_name: 'По левому краю',
      },
      {
        value: 'right',
        display_name: 'По правому краю',
      },
      {
        value: 'center',
        display_name: 'По центру',
      },
    ],
    [],
  );
  const vAligns = useMemo(
    () => [
      {
        value: 'top',
        display_name: 'По верхнему краю',
      },
      {
        value: 'bottom',
        display_name: 'По нижнему краю',
      },
      {
        value: 'middle',
        display_name: 'По центру',
      },
    ],
    [],
  );
  const onFontChange = useCallback(
    (partial) => {
      const oldV = (value && value.font) || {};
      onChange({ font: { ...oldV, ...partial } });
    },
    [onChange, value],
  );
  const onBackgroundChange = useCallback(
    (partial) => {
      const oldV = (value && value.background) || {};
      onChange({ background: { ...oldV, ...partial } });
    },
    [onChange, value],
  );
  const onAligmentChange = useCallback(
    (partial) => {
      const oldV = (value && value.alignment) || {};
      onChange({ alignment: { ...oldV, ...partial } });
    },
    [onChange, value],
  );
  const onWidthChange = useCallback(
    (partial) => {
      const oldV = (value && value.width) || {};
      onChange({ width: { ...oldV, ...partial } });
    },
    [onChange, value],
  );
  const dValue = useMemo(
    () => {
      if (!value) {
        return {
          font: {},
          background: {},
          alignment: {},
          width: {},
        };
      }
      const font = value.font || {};
      const background = value.background || {};
      const alignment = value.alignment || {};
      const width = value.width || {};
      return {
        font,
        background,
        alignment,
        width,
      };
    },
    [value],
  );
  return (
    <>
      <Box sx={{
        border: 1, borderColor: 'grey.500', borderRadius: 2, p: 2, mb: 2,
      }}
      >
        <Typography variant="h6">Шрифт</Typography>
        <Grid container>
          <Grid item sm={6}>
            <EditorControls.Selector
              controlId="format-font-selector"
              label="Font"
              required={false}
              value={dValue.font.name}
              values={dFonts}
              onChange={(e, v) => onFontChange({ name: v })}
            />
          </Grid>
          <Grid item sm={3}>
            <EditorControls.NumerInput
              controlId="format-size-value"
              label="Size"
              value={dValue.font.size}
              onChange={(e, v) => onFontChange({ size: v })}
              precision={0}
              minValue={0}
            />
          </Grid>
          <Grid item sm={3}>
            <EditorControls.StringInput
              controlId="format-color-value"
              label="Цвет"
              value={dValue.font.color}
              onChange={(e, v) => onFontChange({ color: v })}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            display: 'inline-flex', justifyContent: 'space-evenly', width: 1,
          }}
        >
          <EditorControls.CheckboxInput
            controlId="format-bold-value"
            label="Жирный"
            value={dValue.font.bold}
            onChange={(e, v) => onFontChange({ bold: v })}
          />
          <EditorControls.CheckboxInput
            controlId="format-italic-value"
            label="Курсив"
            value={dValue.font.italic}
            onChange={(e, v) => onFontChange({ italic: v })}
          />
          <EditorControls.CheckboxInput
            controlId="format-strike-value"
            label="Зачеркнутый"
            value={dValue.font.strike}
            onChange={(e, v) => onFontChange({ strike: v })}
          />
          <EditorControls.CheckboxInput
            controlId="format-underline-value"
            label="Почеркнутый"
            value={dValue.font.underline}
            onChange={(e, v) => onFontChange({ underline: v })}
          />
        </Box>
      </Box>
      <Grid container>
        <Grid item sm={2}>
          <Box sx={{
            border: 1, borderColor: 'grey.500', borderRadius: 2, p: 2, mb: 2,
          }}
          >
            <Typography variant="h6">Фон</Typography>
            <EditorControls.StringInput
              controlId="format-fgColor-background-value"
              label="Цвет"
              value={dValue.background.fgColor}
              onChange={(e, v) => onBackgroundChange({ fgColor: v })}
            />
          </Box>
        </Grid>
        <Grid item sm={6}>
          <Box sx={{
            border: 1, borderColor: 'grey.500', borderRadius: 2, p: 2, mb: 2,
          }}
          >
            <Typography variant="h6">Выравнивание</Typography>
            <Grid container>
              <Grid item sm={6}>
                <EditorControls.Selector
                  controlId="format-alignment-horizontal-selector"
                  label="Горизонтальное вывавнивание"
                  value={dValue.alignment.horizontal}
                  values={hAligns}
                  onChange={(e, v) => onAligmentChange({ horizontal: v })}
                />
              </Grid>
              <Grid item sm={6}>
                <EditorControls.Selector
                  controlId="format-alignment-vertical-selector"
                  label="Вертикальное выравнивание"
                  value={dValue.alignment.vertical}
                  values={vAligns}
                  onChange={(e, v) => onAligmentChange({ vertical: v })}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item sm={4}>
          <Box sx={{
            border: 1, borderColor: 'grey.500', borderRadius: 2, p: 2, mb: 2,
          }}
          >
            <Typography variant="h6">Размер колонки</Typography>
            <WidthInput
              label="Довжина"
              value={dValue.width.width}
              onChange={(e, v) => onWidthChange({ width: v })}
            />
            <WidthInput
              label="Мінімальна довжина"
              value={dValue.width.min_width}
              onChange={(e, v) => onWidthChange({ min_width: v })}
            />
            <WidthInput
              label="Максимальна довжина"
              value={dValue.width.max_width}
              onChange={(e, v) => onWidthChange({ max_width: v })}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

FormatEditor.propTypes = {
  value: PropTypes.shape({
    font: PropTypes.shape({
      name: PropTypes.string,
      size: PropTypes.number,
      bold: PropTypes.bool,
      italic: PropTypes.bool,
      strike: PropTypes.bool,
      underline: PropTypes.bool,
      color: PropTypes.string,
    }),
    background: PropTypes.shape({
      fgColor: PropTypes.string,
    }),
    alignment: PropTypes.shape({
      horizontal: PropTypes.oneOf(['left', 'right', 'center']),
      vertical: PropTypes.oneOf(['top', 'bottom', 'middle']),
    }),
    width: PropTypes.shape({
      width: PropTypes.string,
      min_width: PropTypes.string,
      max_width: PropTypes.string,
    }),
  }),
  onChange: PropTypes.func.isRequired,
  allowedFonts: PropTypes.arrayOf(PropTypes.string),
};

FormatEditor.defaultProps = {
  value: {},
  allowedFonts: [],
};

export default FormatEditor;
